import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import {
  listModels,
  getModelHealth,
  addModel,
  updateModel,
  removeModel,
  listProviders,
  listEndpoints,
  type ModelRow,
  type RegistryHealth,
  type ProviderRow,
  type ApiRef,
  type ModelConflictSeverity,
} from '@/api/models';
import type { RootStore } from './RootStore';

// ─────────────────────────────────────────────────────────────────────────────
// The registry is not a list of 20 rows — it is a handful of MODEL FAMILIES
// deployed across tenants. `gpt-4.1` exists once per merchant; showing those as
// seven peer rows is the SSMS grid with rounded corners. Group by name, sort
// the platform-wide row first, and let the family carry the identity (provider,
// capabilities, rate) while each deployment carries only what differs.
// ─────────────────────────────────────────────────────────────────────────────

export interface ModelFamily {
  /** ai.AIModel.Name — the family key. */
  name: string;
  rows: ModelRow[];
  /** Deployment slugs seen across the family, deduped. Usually one. */
  deploymentNames: string[];
  provider: string | null;
  /** True when every row in the family agrees on the endpoint. */
  endpointsAgree: boolean;
  activeCount: number;
  agentsWired: number;
  traffic30d: number;
  conflictCount: number;
  worstSeverity: ModelConflictSeverity | null;
  /** Present when at least one row resolved a current rate. */
  hasPricing: boolean;
}

const SEVERITY_RANK: Record<ModelConflictSeverity, number> = {
  Advisory: 0,
  Silent: 1,
  Blocking: 2,
};

export class ModelsStore {
  @observable items: ModelRow[] = [];
  @observable health: RegistryHealth | null = null;
  @observable providers: ProviderRow[] = [];
  @observable endpoints: ApiRef[] = [];

  @observable isLoading = false;
  @observable error: string | null = null;

  @observable search = '';
  @observable activeOnly = false;
  /** Narrow the catalog to rows that have at least one conflict. */
  @observable conflictsOnly = false;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  @action setSearch(v: string): void { this.search = v; }
  @action setActiveOnly(v: boolean): void { this.activeOnly = v; }
  @action setConflictsOnly(v: boolean): void { this.conflictsOnly = v; }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      // Health is advisory — a failure there must not blank the catalog, so it
      // settles independently of the list.
      const [rows, health] = await Promise.all([
        listModels({ search: this.search || undefined, activeOnly: this.activeOnly || undefined }),
        getModelHealth().catch(() => null),
      ]);
      runInAction(() => {
        this.items = rows;
        this.health = health;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load models';
      });
    }
  }

  /** Lookup data for the editor drawer. Loaded once, cheaply. */
  @action
  async fetchLookups(): Promise<void> {
    if (this.providers.length > 0 && this.endpoints.length > 0) return;
    try {
      const [providers, endpoints] = await Promise.all([listProviders(), listEndpoints()]);
      runInAction(() => {
        this.providers = providers;
        this.endpoints = endpoints;
      });
    } catch {
      // Non-fatal: the drawer falls back to raw numeric inputs and says so.
    }
  }

  @computed get filtered(): ModelRow[] {
    return this.conflictsOnly ? this.items.filter((m) => m.conflicts.length > 0) : this.items;
  }

  /** Grouped view — the shape the page actually renders. */
  @computed get families(): ModelFamily[] {
    const map = new Map<string, ModelRow[]>();
    for (const m of this.filtered) {
      const key = (m.name ?? '').trim() || '(unnamed)';
      const bucket = map.get(key);
      if (bucket) bucket.push(m);
      else map.set(key, [m]);
    }

    const families: ModelFamily[] = [];
    for (const [name, rows] of map.entries()) {
      // Platform-wide row (merchantId null) first — it is the one every tenant
      // inherits, so it sets the family's defaults.
      const sorted = [...rows].sort((a, b) => {
        const aGlobal = a.merchantId == null ? 0 : 1;
        const bGlobal = b.merchantId == null ? 0 : 1;
        if (aGlobal !== bGlobal) return aGlobal - bGlobal;
        return (a.merchantId ?? 0) - (b.merchantId ?? 0);
      });

      let worst: ModelConflictSeverity | null = null;
      let conflictCount = 0;
      for (const r of sorted) {
        conflictCount += r.conflicts.length;
        for (const c of r.conflicts) {
          if (worst == null || SEVERITY_RANK[c.severity] > SEVERITY_RANK[worst]) worst = c.severity;
        }
      }

      const endpointIds = new Set(sorted.map((r) => r.apiId));
      const deployments = Array.from(
        new Set(sorted.map((r) => (r.deploymentName ?? '').trim()).filter(Boolean)),
      );

      families.push({
        name,
        rows: sorted,
        deploymentNames: deployments,
        provider: sorted.find((r) => r.provider)?.provider ?? null,
        endpointsAgree: endpointIds.size <= 1,
        activeCount: sorted.filter((r) => r.active).length,
        agentsWired: sorted.reduce((sum, r) => sum + r.agentsWired, 0),
        traffic30d: sorted.reduce((sum, r) => sum + r.traffic30d, 0),
        conflictCount,
        worstSeverity: worst,
        hasPricing: sorted.some((r) => r.pricing != null),
      });
    }

    // Busiest families first — what is actually carrying load deserves the top
    // of the page. Ties fall back to name so the order is stable across loads.
    return families.sort(
      (a, b) => b.traffic30d - a.traffic30d || b.agentsWired - a.agentsWired || a.name.localeCompare(b.name),
    );
  }

  @computed get totalConflicts(): number {
    return this.items.reduce((sum, m) => sum + m.conflicts.length, 0);
  }

  /** Models with 30-day traffic the platform could not cost. Worst first. */
  @computed get unpricedWithTraffic(): RegistryHealth['unpriced'] {
    return this.health?.unpriced ?? [];
  }

  @action
  async create(input: Partial<ModelRow>): Promise<void> {
    await addModel(input);
    await this.fetch();
  }

  @action
  async update(input: Partial<ModelRow> & { id: number }): Promise<void> {
    await updateModel(input);
    await this.fetch();
  }

  @action
  async remove(id: number): Promise<void> {
    await removeModel(id);
    await this.fetch();
  }
}
